import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import { AlcoholSettings, CategorySetting, getAlcoholSettings, getCategorySettings, saveAlcoholSettings, saveCategorySetting } from '../../api';
import { categoryLabel, isAlcoholCategory, refreshFeatures } from '../../features';

const goldButton = { background: 'linear-gradient(135deg, #c9a227, #dbb42c)', color: '#000', fontWeight: 700 };

// Super Admin switches for alcohol-related content (alcohol and happy-hour offers, dining-bill
// discounts covering alcohol, the wine-shop
// directory). Both start off and should stay off until legal approval. Even when on, each alcohol
// offer still needs admin approval and a verified liquor licence. Every change is in the audit log.
const AlcoholControls: React.FC = () => {
  const [saved, setSaved] = useState<AlcoholSettings | null>(null);
  const [draft, setDraft] = useState({ submissionsEnabled: false, displayEnabled: false });
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getAlcoholSettings()
      .then((s) => { setSaved(s); setDraft({ submissionsEnabled: s.submissionsEnabled, displayEnabled: s.displayEnabled }); })
      .catch(() => setMessage({ ok: false, text: 'Could not load the alcohol settings.' }));
  }, []);

  const save = async (next: { submissionsEnabled: boolean; displayEnabled: boolean }, note: string) => {
    setBusy(true);
    setMessage(null);
    try {
      const s = await saveAlcoholSettings(next.submissionsEnabled, next.displayEnabled, note || undefined);
      setSaved(s);
      setDraft({ submissionsEnabled: s.submissionsEnabled, displayEnabled: s.displayEnabled });
      setReason('');
      refreshFeatures();
      setMessage({ ok: true, text: `Saved. Submissions ${s.submissionsEnabled ? 'on' : 'off'}, public display ${s.displayEnabled ? 'on' : 'off'}.` });
    } catch {
      setMessage({ ok: false, text: 'Could not save the alcohol settings.' });
    } finally {
      setBusy(false);
    }
  };

  const turningOn = !!saved && ((draft.submissionsEnabled && !saved.submissionsEnabled) || (draft.displayEnabled && !saved.displayEnabled));
  const changed = !!saved && (draft.submissionsEnabled !== saved.submissionsEnabled || draft.displayEnabled !== saved.displayEnabled);

  const emergencyStop = () => {
    if (!window.confirm('Hide all alcohol-related content now? Alcohol and happy-hour offers and the wine-shop directory disappear from the site immediately, and partners can no longer submit them.')) return;
    save({ submissionsEnabled: false, displayEnabled: false }, reason || 'Emergency disable');
  };

  return (
    <Box sx={{ border: '1px solid #4b3a12', borderRadius: 2, p: 2.5, mb: 4, background: 'rgba(201,162,39,0.05)' }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={1}>
        <Box>
          <Typography variant="h6" color="#e6e6e6">Alcohol Offer Controls</Typography>
          <Typography variant="body2" color="#8b8b95">
            Off by default. Turn on only after legal approval. Alcohol offers still need your approval and a verified venue licence before they're shown.
          </Typography>
        </Box>
        <Button color="error" variant="contained" disabled={busy || !saved || (!saved.submissionsEnabled && !saved.displayEnabled)} onClick={emergencyStop}>
          Emergency: Hide All Alcohol Content
        </Button>
      </Box>
      {message && <Alert severity={message.ok ? 'success' : 'error'} sx={{ my: 1.5 }}>{message.text}</Alert>}
      <Box display="flex" flexDirection="column" gap={0.5} my={1.5}>
        <FormControlLabel
          control={<Switch checked={draft.submissionsEnabled} onChange={(e) => setDraft({ ...draft, submissionsEnabled: e.target.checked })} disabled={!saved || busy} />}
          label={<span><b>Allow submissions</b>: partners can publish alcohol and happy-hour offers (each waits for your approval)</span>}
        />
        <FormControlLabel
          control={<Switch checked={draft.displayEnabled} onChange={(e) => setDraft({ ...draft, displayEnabled: e.target.checked })} disabled={!saved || busy} />}
          label={<span><b>Show publicly</b>: approved alcohol offers from licence-verified venues, and wine shops, appear on the site</span>}
        />
      </Box>
      {changed && (
        <Box display="flex" gap={1.5} alignItems="flex-start" flexWrap="wrap">
          <TextField size="small" label={turningOn ? 'Legal approval reference (required)' : 'Reason (optional)'} value={reason} onChange={(e) => setReason(e.target.value)} sx={{ flex: '1 1 320px' }} inputProps={{ maxLength: 200 }} />
          <Button variant="contained" sx={goldButton} disabled={busy || (turningOn && !reason.trim())} onClick={() => save(draft, reason.trim())}>Save</Button>
          <Button onClick={() => saved && setDraft({ submissionsEnabled: saved.submissionsEnabled, displayEnabled: saved.displayEnabled })} disabled={busy}>Cancel</Button>
        </Box>
      )}
      {saved?.updatedAt && (
        <Typography fontSize={12} color="#8b8b95" mt={1}>
          Last changed {new Date(saved.updatedAt).toLocaleString('en-IN')}{saved.updatedBy ? ` by ${saved.updatedBy}` : ''}. Full history in the Audit Log tab.
        </Typography>
      )}
    </Box>
  );
};

export default AlcoholControls;

// Super Admin switches for each offer category. A switched-off category's offers disappear from the
// site at once and partners can't publish new ones; drafts are kept. Alcohol and happy-hour offers
// also need the alcohol switches above.
export const CategoryControls: React.FC = () => {
  const [settings, setSettings] = useState<CategorySetting[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    getCategorySettings().then(setSettings).catch(() => setError('Could not load the category settings.'));
  }, []);

  const toggle = async (c: CategorySetting) => {
    const label = categoryLabel(c.category);
    if (c.enabled && !window.confirm(`Switch off "${label}"? Its offers disappear from the site now and partners can't publish new ones.`)) return;
    setBusy(c.category);
    setError(null);
    try {
      setSettings(await saveCategorySetting(c.category, !c.enabled));
      refreshFeatures();
    } catch {
      setError('Could not save the category setting.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box sx={{ border: '1px solid #2a2f38', borderRadius: 2, p: 2.5, mb: 4 }}>
      <Typography variant="h6" color="#e6e6e6">Offer Categories</Typography>
      <Typography variant="body2" color="#8b8b95" mb={1.5}>Switch a category off to hide its offers and stop new submissions. Changes are in the Audit Log.</Typography>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }} gap={0.5}>
        {(settings ?? []).map((c) => (
          <FormControlLabel
            key={c.category}
            control={<Switch checked={c.enabled} disabled={busy !== null} onChange={() => toggle(c)} />}
            label={<span>{categoryLabel(c.category)}{isAlcoholCategory(c.category) ? <Typography component="span" fontSize={12} color="#8b8b95"> (also needs alcohol switches)</Typography> : null}</span>}
          />
        ))}
      </Box>
    </Box>
  );
};
